import React from 'react';
import {
  Action, ActionProps, Message, ModalSize, Portal, useModal,
} from '@doveit/bricks';
import useIndividualGoal from '../../hooks/use-individual-goal/useIndividualGoal';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { Agent, IndividualGoal } from '../../../providers/api/dtos';
import { AgentGoalSimulationView, AgentGoalSimulationViewProps } from '../agent-goal-simulation-view/AgentGoalSimulationView';

interface ChildFnProps {
  openEditModal: VoidFunction,
  isLoading: boolean,
}

export interface EditAgentGoalActionProps {
  agentId: NonNullable<Agent['id']>,
  label?: ActionProps['label'],
  size?: ActionProps['size'],
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
  onGoalAccepted?: AgentGoalSimulationViewProps['onGoalAccepted']
}

export const LOAD_GOAL_ERROR_MESSAGE = 'Non è stato possibile caricare le informazioni relative all\'obiettivo di guadagno.';

export const EditAgentGoalAction: React.FC<EditAgentGoalActionProps> = ({
  agentId,
  label = 'Modifica obiettivo',
  size = 'S',
  onGoalAccepted,
  children,
}) => {
  const {
    data: existingGoal,
    isLoading: isExistingGoalLoading,
    error: existingGoalError,
    mutate: mutateExistingGoal,
  } = useIndividualGoal(agentId);
  const editAgentGoalModal = useModal();

  const openEditModal = React.useCallback(() => {
    editAgentGoalModal.open();
  }, [editAgentGoalModal]);

  const onInternalGoalAccepted = React.useCallback((acceptedGoal: IndividualGoal) => {
    editAgentGoalModal.close();
    mutateExistingGoal();
    onGoalAccepted?.(acceptedGoal);
  }, [editAgentGoalModal, mutateExistingGoal, onGoalAccepted]);

  if (existingGoalError && existingGoalError.status === 404) {
    return null;
  }

  return (
    <>
      {
        children
          ? children({ isLoading: isExistingGoalLoading, openEditModal })
          : (
            <Action
              label={label}
              aria-label="Pulsante per modificare l'obiettivo di guadagno"
              loading={isExistingGoalLoading}
              onClick={openEditModal}
              size={size}
            />
          )
      }
      <Portal>
        <SimpleModal
          {...editAgentGoalModal}
          title="Modifica l'obiettivo di guadagno"
          aria-label="Modifica obiettivo di guadagno"
          loading={isExistingGoalLoading}
          size={ModalSize.LARGE}
        >
          {(existingGoalError && existingGoalError.status !== 404) && (
            <Message
              message={LOAD_GOAL_ERROR_MESSAGE}
              type="critical"
            />
          )}
          {!existingGoalError && existingGoal && (
            <AgentGoalSimulationView
              hideTutorial
              agentId={agentId}
              goal={existingGoal}
              onGoalAccepted={onInternalGoalAccepted}
            />
          )}
        </SimpleModal>
      </Portal>
    </>
  );
};

export default EditAgentGoalAction;
